import { useEffect, useRef } from "react";

import { Observation } from "@medplum/fhirtypes";

import {
  LABOR_FLOW_SHEET_QUESTIONNAIRE_MAP,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import EditBirthingLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditBirthingLaborRow";
import EditCervicalExamLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditCervicalExamLaborRow";
import EditContractionsLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditContractionsLaborRow";
import EditFetalHeartRateLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditFetalHeartRateLaborRow";
import EditInputOutputLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditInputOutputLaborRow";
import EditIntakeLaborFlowsheetRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditIntakeLaborFlowsheetRow";
import EditLaborVitalsLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditLaborVitalsLaborRow";
import EditMembranesLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditMembranesLaborRow";
import EditPositionLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditPositionLaborRow";
import EditRomLaborRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/EditRomLaborRow";
import {
  LABOR_FLOWSHEET_EDIT_UNKNOWN_QUESTIONNAIRE_MESSAGE,
  LABOR_FLOWSHEET_EDIT_UNKNOWN_QUESTIONNAIRE_TITLE,
} from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/use-labor-flowsheet-edit-route-context";
import { toast } from "@/utils/notifications";

interface EditLaborRowProps {
  observation?: Observation;
  onClose?: () => void;
}

export default function EditLaborRow({ observation, onClose }: EditLaborRowProps) {
  const questionnaireDisplayName = observation?.code?.coding?.[0]?.display ?? null;
  const questionnaireName =
    LABOR_FLOW_SHEET_QUESTIONNAIRE_MAP[
      questionnaireDisplayName as keyof typeof LABOR_FLOW_SHEET_QUESTIONNAIRE_MAP
    ] ?? null;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (questionnaireName) {
      return;
    }

    toast({
      type: "error",
      title: LABOR_FLOWSHEET_EDIT_UNKNOWN_QUESTIONNAIRE_TITLE,
      message: LABOR_FLOWSHEET_EDIT_UNKNOWN_QUESTIONNAIRE_MESSAGE,
      autoClose: false,
    });
    onCloseRef.current?.();
  }, [questionnaireName]);

  if (!questionnaireName || !observation) {
    return null;
  }

  if (questionnaireName === QuestionnaireType.FetalHeartRate) {
    return (
      <EditFetalHeartRateLaborRow
        onClose={onClose}
        observation={observation}
        questionnaireDisplayName={questionnaireDisplayName ?? "Fetal Heart Rate"}
      />
    );
  }

  if (questionnaireName === QuestionnaireType.LaborVitals) {
    return <EditLaborVitalsLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.Contractions) {
    return <EditContractionsLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.CervicalExam) {
    return <EditCervicalExamLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.Membranes) {
    return <EditMembranesLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.RuptureOfMembranes) {
    return <EditRomLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.Position) {
    return <EditPositionLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.InputOutput) {
    return <EditInputOutputLaborRow onClose={onClose} observation={observation} />;
  }

  if (questionnaireName === QuestionnaireType.Birthing) {
    return <EditBirthingLaborRow onClose={onClose} observation={observation} />;
  }

  return (
    <EditIntakeLaborFlowsheetRow
      onClose={onClose}
      observation={observation}
      questionnaireName={questionnaireName}
      questionnaireDisplayName={questionnaireDisplayName}
    />
  );
}
